import React, { useEffect, useState, useMemo } from "react";
import toastr from "toastr";
import BondOrders from "./BondOrders";
import IPOOrders from "./IPOOrders";
import {
  BOND_ORDER_HISTORY_ENDPOINTS,
  BondOrderHistoryResponse,
  CalendarCashflowValues,
  IPOOrderHistoryResponse,
  calculateRowHeight,
  computeHeatmapData,
  dedupeBondOrders,
  disclaimerPoints,
  getBondOrderSide,
  getOrderInvestmentAmount,
  normalizeBondOrdersPayload,
} from "./helpers";
import { makeGETRequest, makeGETRequestOnUserService } from "../../../Api/api";
import { Moment } from "moment";
import moment from "moment";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ExcelImg from "../../../images/excel-green.svg";
import { TabPanel } from "../../../pages/BidWars";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import DialogContent from "@mui/material/DialogContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import DialogActions from "@mui/material/DialogActions";
import { is_gsec_sdl_tBill } from "../../../Helpers/functions";
import ExcelJS from "exceljs";

const TAB_OPTIONS = [
  {
    title: "Bonds",
    component: (currentUser: any) => <BondOrders currentUser={currentUser} />,
  },
  {
    title: "Public Issue",
    component: (currentUser: any) => <IPOOrders currentUser={currentUser} />,
  },
];

export default function OrderHistoryTable(props: any) {
  const { currentUser } = props;
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [downloadOption, setDownloadOption] = useState<"viewAll" | "currentFY">(
    "viewAll"
  );
  const [bondOrders, setBondOrders] = useState<BondOrderHistoryResponse[]>([]);
  const [ipoOrders, setIPOOrders] = useState<IPOOrderHistoryResponse[]>([]);
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    let mounted = true;
    const fetchProfile = async () => {
      try {
        const response = await makeGETRequestOnUserService(
          `user/get/${currentUser.uuid}/`
        );

        if (mounted && response.status === 200) {
          const profile = response.data;
          setProfile(profile);
        }
      } catch (error) {
        if (mounted) {
          toastr.error("Failed to fetch profile");
        }
      }
    };
    if (currentUser.uuid) {
      fetchProfile();
    }
    return () => {
      mounted = false;
    };
  }, [currentUser.uuid]);

  useEffect(() => {
    let mounted = true;
    const fetchBondOrders = async () => {
      try {
        const responses = await Promise.allSettled(
          BOND_ORDER_HISTORY_ENDPOINTS.map((buildUrl) =>
            makeGETRequestOnUserService(buildUrl(currentUser.uuid))
          )
        );

        const fulfilledResponses = responses.filter(
          (
            response
          ): response is PromiseFulfilledResult<{ data?: unknown }> =>
            response.status === "fulfilled"
        );

        if (!fulfilledResponses.length) {
          throw new Error("Failed to fetch bond orders");
        }

        const combinedOrders = dedupeBondOrders(
          fulfilledResponses.flatMap((response) =>
            normalizeBondOrdersPayload(response.value.data)
          )
        );

        if (mounted) {
          setBondOrders(combinedOrders);
        }
      } catch (error) {
        if (mounted) {
          toastr.error("Failed to fetch bond orders");
        }
      }
    };
    if (currentUser.uuid) {
      fetchBondOrders();
    }
    return () => {
      mounted = false;
    };
  }, [currentUser.uuid]);

  useEffect(() => {
    let mounted = true;
    const fetchIPOOrders = async () => {
      try {
        const response = await makeGETRequest(
          `/ipo/order/history/${currentUser.uuid}/`
        );
        if (mounted && response.status === 200) {
          setIPOOrders(response.data);
        }
      } catch (error) {
        if (mounted) {
          toastr.error("Failed to fetch ipo orders");
        }
      }
    };
    if (currentUser.uuid) {
      fetchIPOOrders();
    }
    return () => {
      mounted = false;
    };
  }, [currentUser.uuid]);

  const customerDetails = useMemo(
    () => [
      {
        key: "Name of Customer:",
        value: `${profile?.firstName} ${profile?.middleName || ""} ${
          profile?.lastName
        }`,
      },
      { key: "PAN:", value: profile?.panCard.panCardNumber },
      { key: "Mobile:", value: profile?.mobileNumber },
      { key: "Email-id:", value: profile?.emailId },
    ],
    [profile]
  );

  const heatmapData = useMemo(
    () =>
      computeHeatmapData(
        bondOrders.filter((order) => getBondOrderSide(order.bid_offer) === "Buy")
      ),
    [bondOrders]
  );

  const flattenedData = useMemo(() => {
    const aggregatedData = new Map<
      string,
      CalendarCashflowValues & { date: Moment; amountAfterTds: number }
    >();

    heatmapData.forEach(({ datesOfMonth }) => {
      datesOfMonth.forEach(({ date, cashflows }) => {
        cashflows.forEach((cashflow) => {
          const key = `${cashflow.isin}-${date.format("YYYY-MM-DD")}-${
            cashflow.type
          }`;
          const existing = aggregatedData.get(key);

          if (existing) {
            existing.amount += cashflow.amount;
            existing.noOfBonds += cashflow.noOfBonds;
            existing.amountAfterTds +=
              cashflow.type === "Interest" &&
              !(
                is_gsec_sdl_tBill(cashflow.securityType) ||
                cashflow.taxCategory === "Tax Free"
              )
                ? cashflow.amount * 0.9
                : cashflow.amount;
          } else {
            aggregatedData.set(key, {
              ...cashflow,
              date,
              amountAfterTds:
                cashflow.type === "Interest" &&
                !(
                  is_gsec_sdl_tBill(cashflow.securityType) ||
                  cashflow.taxCategory === "Tax Free"
                )
                  ? cashflow.amount * 0.9
                  : cashflow.amount,
            });
          }
        });
      });
    });

    return Array.from(aggregatedData.values());
  }, [heatmapData]);

  const { fyStart, fyEnd } = useMemo(() => {
    const currentYear = moment().year();
    return {
      fyStart: moment(`${currentYear}-04-01`),
      fyEnd: moment(`${currentYear + 1}-03-31`),
    };
  }, []);

  const filteredInterest = useMemo(
    () =>
      flattenedData.filter((row) =>
        downloadOption === "currentFY"
          ? moment(row.date).isBetween(fyStart, fyEnd, "day", "[]")
          : true
      ),
    [downloadOption, flattenedData, fyEnd, fyStart]
  );

  const sortedData = useMemo(
    () =>
      [...filteredInterest].sort((a, b) => a.date.valueOf() - b.date.valueOf()),
    [filteredInterest]
  );

  const handleExportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();

    const interestSheet = workbook.addWorksheet("Indicative Interest");

    let currentInterestRow = 1;
    customerDetails.forEach((detail) => {
      const row = interestSheet.getRow(currentInterestRow);
      row.getCell(1).value = detail.key;
      row.getCell(1).font = { bold: true };
      row.getCell(2).value = detail.value;
      currentInterestRow++;
    });
    currentInterestRow++;

    if (downloadOption === "currentFY") {
      const headerInterestRow = interestSheet.getRow(currentInterestRow);
      interestSheet.mergeCells(`A${currentInterestRow}:M${currentInterestRow}`);
      headerInterestRow.getCell(
        1
      ).value = `Indicative Interest Received Statement for the Financial Year ${fyStart.format(
        "YYYY"
      )}-${fyEnd.format("YYYY")} (${fyStart.format(
        "DD/MM/YYYY"
      )} to ${fyEnd.format("DD/MM/YYYY")})`;
      headerInterestRow.getCell(1).font = { bold: true };
      headerInterestRow.height = 20;
      currentInterestRow++;
    }

    const timestampInterestRow = interestSheet.getRow(currentInterestRow);
    interestSheet.mergeCells(`A${currentInterestRow}:M${currentInterestRow}`);
    timestampInterestRow.getCell(
      1
    ).value = `Statement generated Date and Time Stamp: ${moment().format(
      "DD-MM-YYYY h:mm a"
    )}`;
    timestampInterestRow.getCell(1).font = { bold: true };
    timestampInterestRow.height = 20;
    currentInterestRow += 3;

    interestSheet.columns = [
      { key: "index", width: 20 },
      { key: "isin", width: 30 },
      { key: "securityName", width: 80 },
      { key: "coupon", width: 15 },
      { key: "qty", width: 10 },
      { key: "maturityDate", width: 40 },
      { key: "frequency", width: 30 },
      { key: "paymentDate", width: 30 },
      { key: "amount", width: 25 },
      { key: "amountAfterTds", width: 35 },
      { key: "type", width: 20 },
    ];

    interestSheet.getRow(currentInterestRow).values = [
      "No.",
      "ISIN",
      "Security Name",
      "Coupon(%)",
      "Qty",
      "Maturity Date / Call Date / Put Date",
      "Interest Payment Frequency",
      "Interest Payment Date",
      "Interest Amount (Rs.)",
      "Interest Amount (Rs.) Post TDS",
      "Type",
    ];

    interestSheet.getRow(currentInterestRow).font = { bold: true };
    interestSheet.getRow(currentInterestRow).eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" },
      };
    });
    currentInterestRow++;

    sortedData.forEach((row, index) => {
      const newRow = interestSheet.addRow({
        index: index + 1,
        isin: row.isin,
        issuerName: row.issuerName,
        securityName: row.securityName,
        coupon: row.coupon,
        qty: row.noOfBonds,
        maturityDate: `${row.maturityOrCallDate} (${row.maturityOrCallDateType})`,
        frequency: row.frequency,
        paymentDate: row.date.format("DD MMM YYYY"),
        amount: row.amount,
        amountAfterTds: row.amountAfterTds,
        type: row.type,
      });

      newRow.alignment = { horizontal: "left" };
      newRow.getCell("amount").numFmt = "₹#,##0.00";
      newRow.getCell("amountAfterTds").numFmt = "₹#,##0.00";
      newRow.getCell("amountAfterTds").font = {
        bold: true,
      };
      newRow.getCell("paymentDate").font = {
        bold: true,
      };
      const rowFill = {
        type: "pattern" as const,
        pattern: "solid" as const,
        fgColor: {
          argb:
            newRow.getCell("type").value === "Interest"
              ? "FFEEF8EE"
              : "FFF1F0FD",
        },
      };
      newRow.getCell("type").fill = rowFill;
      newRow.getCell("amountAfterTds").fill = rowFill;
    });

    const totalInterestColumnWidth = interestSheet.columns.reduce(
      (sum, column) => sum + (column.width || 0),
      0
    );

    currentInterestRow = interestSheet.rowCount + 3;

    const disclaimerTitleInterestRow = interestSheet.getRow(currentInterestRow);
    disclaimerTitleInterestRow.getCell(1).value = "Disclaimers:";
    disclaimerTitleInterestRow.getCell(1).font = { bold: true };
    disclaimerTitleInterestRow.height = 20;
    currentInterestRow++;

    disclaimerPoints.forEach((text) => {
      const row = interestSheet.getRow(currentInterestRow);
      interestSheet.mergeCells(`A${currentInterestRow}:K${currentInterestRow}`);

      const cell = row.getCell(1);
      cell.value = text;
      cell.alignment = {
        wrapText: true,
        vertical: "top",
        horizontal: "left",
      };
      cell.font = {
        name: "Arial",
        size: 10,
        scheme: "none",
      };

      cell.border = {};

      const rowHeight = calculateRowHeight(text, totalInterestColumnWidth);

      row.height = rowHeight + 4;

      row.hidden = false;
      currentInterestRow++;
    });

    const buySheet = workbook.addWorksheet("Buy Transactions");

    let currentBuyRow = 1;
    customerDetails.forEach((detail) => {
      const row = buySheet.getRow(currentBuyRow);
      row.getCell(1).value = detail.key;
      row.getCell(1).font = { bold: true };
      row.getCell(2).value = detail.value;
      currentBuyRow++;
    });
    currentBuyRow++;

    if (downloadOption === "currentFY") {
      const headerBuyRow = buySheet.getRow(currentBuyRow);
      buySheet.mergeCells(`A${currentBuyRow}:M${currentBuyRow}`);
      headerBuyRow.getCell(
        1
      ).value = `Statement of Securities Purchase / Securities allotted during the Financial Year ${fyStart.format(
        "YYYY"
      )}-${fyEnd.format("YYYY")} (${fyStart.format(
        "DD/MM/YYYY"
      )} to ${fyEnd.format("DD/MM/YYYY")})`;
      headerBuyRow.getCell(1).font = { bold: true };
      headerBuyRow.height = 20;
      currentBuyRow++;
    }

    const timestampBuyRow = buySheet.getRow(currentBuyRow);
    buySheet.mergeCells(`A${currentBuyRow}:M${currentBuyRow}`);
    timestampBuyRow.getCell(
      1
    ).value = `Statement generated Date and Time Stamp: ${moment().format(
      "DD-MM-YYYY h:mm a"
    )}`;
    timestampBuyRow.getCell(1).font = { bold: true };
    timestampBuyRow.height = 20;
    currentBuyRow += 3;

    buySheet.columns = [
      { key: "index", width: 20 },
      { key: "isin", width: 30 },
      { key: "type", width: 20 },
      { key: "securityName", width: 80 },
      { key: "coupon", width: 15 },
      { key: "qty", width: 10 },
      { key: "settlementDate", width: 20 },
      { key: "maturityOrCallDate", width: 40 },
      { key: "price", width: 15 },
      { key: "yield", width: 15 },
      { key: "settlementOrAllotmentAmount", width: 40 },
      { key: "principalAmount", width: 25 },
      { key: "modifiedAccruedInterest", width: 25 },
      { key: "stampDuty", width: 25 },
    ];

    buySheet.getRow(currentBuyRow).values = [
      "No.",
      "ISIN",
      "Type",
      "Security Name",
      "Coupon( % )",
      "Qty",
      "Settlement Date",
      "Maturity Date / Call Date / Put Date",
      "Price",
      "Yield",
      "Settlement / Allotment Amount (Rs.)",
      "Principal Amount (Rs.)",
      "Accrued Interest (Rs.)",
      "Stamp Duty (Rs.)",
    ];
    buySheet.getRow(currentBuyRow).font = { bold: true };
    buySheet.getRow(currentBuyRow).eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" },
      };
    });
    currentBuyRow++;

    // Combine and sort transactions as before
    let buyTransactions: Array<any> = [];
    if (bondOrders) {
      bondOrders
        .filter(
          (order) =>
            getBondOrderSide(order.bid_offer) === "Buy" &&
            order.deal_status === "Settled"
        )
        .forEach((order) => {
          buyTransactions.push({
            isin: order.isin,
            type: order.security_type,
            securityName: order.security_name,
            coupon: order.security_name.split(" ")[0],
            qty: order.no_of_bonds,
            settlementDate: moment(order.settlement_date),
            maturityOrCallDate: `${
              order.last_call_date
                ? order.last_call_date
                : order.put_options?.length &&
                  moment(order.put_options[0].date, "YYYY-MM-DD").isSame(
                    moment(order.last_maturity_date, "DD/MM/YYYY"),
                    "day"
                  )
                ? order.put_options[0].date
                : order.last_maturity_date
            } (${
              order.last_call_date
                ? "Call Date"
                : order.put_options?.length &&
                  moment(order.put_options[0].date, "YYYY-MM-DD").isSame(
                    moment(order.last_maturity_date, "DD/MM/YYYY"),
                    "day"
                  )
                ? "Put Date"
                : "Maturity Date"
            })`,
            price: Number(order.sell_price),
            yield: `${order.sell_yield}%`,
            settlementOrAllotmentAmount: getOrderInvestmentAmount(order),
            principalAmount: Number(order.principal_amount),
            modifiedAccruedInterest: Number(order.modified_accrued_interest),
            stampDuty: Number(order.stamp_duty),
          });
        });
    }
    if (ipoOrders) {
      ipoOrders
        .filter(
          (order) =>
            ![
              "Pending Allotment",
              "Unsuccessful Allotment",
              "Cancelled",
            ].includes(order.allotmentstatus)
        )
        .forEach((order) => {
          buyTransactions.push({
            isin: order.isin ? order.isin : "To be Alloted",
            type: "Public Issue",
            securityName: order.ipo_security_name,
            coupon: order.isin
              ? order.ipo_security_name.split(" ")[0]
              : "To be Alloted",
            qty: Number(order.bids?.[0]?.quantity).toFixed(0) || 0,
            settlementDate: moment(order.insertedAt),
            maturityOrCallDate: order.isin
              ? `${
                  order.ipo_security_name.match(
                    /(\d{1,2}\s[A-Za-z]{3}\s\d{4})$/i
                  )?.[0]
                } (Maturity Date)`
              : "To be Alloted",
            price: "To be Alloted",
            yield: "To be Alloted",
            settlementOrAllotmentAmount: Number(order.chequeamount),
            principalAmount: Number(order.chequeamount),
            modifiedAccruedInterest: Number(0),
            stampDuty: Number(0),
          });
        });
    }
    if (downloadOption === "currentFY") {
      buyTransactions = buyTransactions.filter((tx) =>
        moment(tx.settlementDate).isBetween(fyStart, fyEnd, "day", "[]")
      );
    }
    buyTransactions.sort(
      (a, b) => a.settlementDate.valueOf() - b.settlementDate.valueOf()
    );

    buyTransactions.forEach((tx, idx) => {
      const row = buySheet.addRow({
        index: idx + 1,
        isin: tx.isin,
        type: tx.type,
        securityName: tx.securityName,
        coupon: tx.coupon,
        qty: tx.qty,
        settlementDate: tx.settlementDate.format("DD MMM YYYY"),
        maturityOrCallDate: tx.maturityOrCallDate,
        price: tx.price,
        yield: tx.yield,
        settlementOrAllotmentAmount: tx.settlementOrAllotmentAmount,
        principalAmount: tx.principalAmount,
        modifiedAccruedInterest: tx.modifiedAccruedInterest,
        stampDuty: tx.stampDuty,
      });
      row.alignment = { horizontal: "left" };
      row.getCell("type").value === "Public Issue"
        ? (row.getCell("type").fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF1F0FD" },
          })
        : (row.getCell("type").fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFEEF8EE" },
          });
    });

    [
      "settlementOrAllotmentAmount",
      "principalAmount",
      "modifiedAccruedInterest",
      "stampDuty",
      "price",
    ].forEach((key) => {
      buySheet.getColumn(key).numFmt = "₹#,##0.00";
    });

    const totalBuyColumnWidth = buySheet.columns.reduce(
      (sum, column) => sum + (column.width || 0),
      0
    );
    currentBuyRow = buySheet.rowCount + 3;
    const disclaimerTitleBuyRow = buySheet.getRow(currentBuyRow);
    disclaimerTitleBuyRow.getCell(1).value = "Disclaimers:";
    disclaimerTitleBuyRow.getCell(1).font = { bold: true };
    disclaimerTitleBuyRow.height = 20;
    currentBuyRow++;
    disclaimerPoints.forEach((text) => {
      const row = buySheet.getRow(currentBuyRow);
      buySheet.mergeCells(`A${currentBuyRow}:M${currentBuyRow}`);
      const cell = row.getCell(1);
      cell.value = text;
      cell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };
      cell.font = { name: "Arial", size: 10, scheme: "none" };
      const rowHeight = calculateRowHeight(text, totalBuyColumnWidth);
      row.height = rowHeight + 4;
      currentBuyRow++;
    });

    // Finalize export below
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Order_History_Statement_${moment().format(
      "YYYYMMDD"
    )}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setDialogOpen(false);
    setAgreeChecked(false);
  };

  return (
    <>
      <Box sx={{ maxWidth: "90%", mx: "auto", zoom: 0.9 }}>
        <Box
          sx={{
            mx: [2, 4],
            mb: 4,
            px: 2,
            pt: 2,
            pb: 0,
            bgcolor: "#FFFFFF",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {TAB_OPTIONS.map((item, index) => (
                <Button
                  key={index}
                  variant={tabValue === index ? "contained" : "outlined"}
                  onClick={() => setTabValue(index)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    color: "secondary.main",
                    fontWeight: 600,
                    "& .MuiButton-endIcon": {
                      height: 20,
                    },
                  }}
                  endIcon={
                    <Box
                      component="span"
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: "secondary.main",
                        color: "#FFFFFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px !important",
                      }}
                    >
                      {index === 0
                        ? bondOrders
                          ? bondOrders.length
                          : 0
                        : ipoOrders
                        ? ipoOrders.length
                        : 0}
                    </Box>
                  }
                >
                  {item.title}
                </Button>
              ))}
            </Box>
            <Button
              variant="outlined"
              color="success"
              sx={{ borderRadius: 2, fontWeight: 600, position: "relative" }}
              onClick={() => setDialogOpen(true)}
              startIcon={
                <Box
                  component="img"
                  src={ExcelImg}
                  sx={{ width: 18, height: 18 }}
                  alt="Excel Icon"
                />
              }
            >
              My Statements
            </Button>
          </Box>
          <Box>
            {TAB_OPTIONS.map((item, index) => (
              <TabPanel value={tabValue} key={index} index={index}>
                {item.component(currentUser)}
              </TabPanel>
            ))}
          </Box>
        </Box>
      </Box>
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setAgreeChecked(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Download Statements
          <br />
          <Typography variant="subtitle2" color="textSecondary">
            Statements for Indicative Interest Received, Securities Purchase
            /Subscribed and Securities Sold / Matured during the current
            Financial Year.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreeChecked}
                onChange={(e) => setAgreeChecked(e.target.checked)}
                color="primary"
              />
            }
            sx={{ alignItems: "flex-start" }}
            label={
              <Typography
                variant="body2"
                component="div"
                sx={{ whiteSpace: "pre-line", lineHeight: 1.5, mt: 1 }}
              >
                {`I/We agree, acknowledge and confirm the following:
• I/We shall use this report solely for personal consumption and shall not distribute, share or disclose it to any third party.
• I/We acknowledge that the data contained in the report is derived from the Cash Flow as per the relevant Issue Document and is provided for informational purposes only. Such data shall not be used, presented or relied upon for any other purpose.
• SMEST Capital provides no tax advice, and I/We shall not construe any information contained in this report as tax advice.
• I/We understand and agree that for any queries or concerns related to the report, I/We shall directly contact the Issuer Company. Furthermore, for any queries related to taxation, I/We shall take independent advice from a qualified tax consultant.
• SMEST Capital, its directors, employees and affiliates disclaim any responsibility for the accuracy or completeness of the data contained in this report. I/We agree not to rely on such data for making any investment, financial or tax-related decisions.
• I/We acknowledge that it is my/our sole responsibility to independently verify the accuracy and reliability of the information provided in the report with relevant source documents. Under no circumstances shall SMEST Capital, its directors or affiliates be held liable for any inaccuracies, omissions or consequences from the use of the data.`}
              </Typography>
            }
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="download-option-label">Select Option</InputLabel>
            <Select
              labelId="download-option-label"
              value={downloadOption}
              onChange={(e) =>
                setDownloadOption(e.target.value as "viewAll" | "currentFY")
              }
              label="Select Option"
            >
              <MenuItem value="currentFY">
                Current Financial Year ({fyStart.format("DD/MM/YYYY")} -{" "}
                {fyEnd.format("DD/MM/YYYY")})
              </MenuItem>
              <MenuItem value="viewAll">View All</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleExportToExcel}
            disabled={!agreeChecked}
            variant="contained"
            color="primary"
            sx={{
              background: agreeChecked
                ? "linear-gradient(to right, #add100, #7b920a)"
                : "default",
              color: "#FFFFFF",
              boxShadow:
                "-8px 8px 16px rgba(205, 205, 205, 0.2), 8px -8px 16px rgba(205, 205, 205, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.9), 8px 8px 20px rgba(205, 205, 205, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.3), inset -1px -1px 2px rgba(205, 205, 205, 0.5)",
              py: 1,
              px: 2,
              borderRadius: 2,
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
